import { useState, useEffect } from "react";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useRealTimeNotifications } from "../../hooks/useRealTimeNotifications";
import { useNotificationSound } from "../../hooks/useNotificationSound";
import { useIsMobile } from "../../hooks/useMediaQuery";
import MobileNotificationOverlay from "./MobileNotificationOverlay";
import NotificationCard from "./NotificationCard";

// Definir la interfaz para una notificación
interface Notification {
  id: number;
  usuario_id: number;
  tipo: string;
  mensaje: string;
  url: string | null;
  leido: boolean;
  created_at: string;
  partners_source?: boolean;
  source_display?: string;
}

// Helper function to parse service reminder messages and extract metadata
const parseServiceReminder = (mensaje: string) => {
  const reminderMatch = mensaje.match(/\[servicio-(\d+)-(\w+)\]/);
  if (reminderMatch) {
    const servicioId = reminderMatch[1];
    const evento = reminderMatch[2];
    const cleanMessage = mensaje.replace(/\[servicio-\d+-\w+\]/, '').trim();
    return { servicioId, evento, cleanMessage, isServiceReminder: true };
  }
  return { cleanMessage: mensaje, isServiceReminder: false };
};


export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user, token } = useAuth();
  const { playSound } = useNotificationSound();
  const isMobile = useIsMobile();

  // Hook para notificaciones en tiempo real
  useRealTimeNotifications(
    // Callback cuando llega nueva notificación
    async (newNotification) => {
      // Añadir la nueva notificación al inicio de la lista
      setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Mantener máximo 50
      
      // Reproducir sonido
      await playSound();
      
      // Nueva notificación añadida
    },
    true // habilitar sonido
  );

  const unreadNotifications = notifications.filter(n => !n.leido);
  const hasUnread = unreadNotifications.length > 0;

  function toggleDropdown() {
    setIsOpen(!isOpen);
  }

  function closeDropdown() {
    setIsOpen(false);
  }

  const handleClick = () => {
    toggleDropdown();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/notifications/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.data);
      }
    } catch (error) {
      console.error('Error al actualizar notificaciones:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      if (user && token) { // Usar 'token' directamente del contexto
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/api/notifications/all`, {
            headers: {
              'Authorization': `Bearer ${token}` // Usar 'token'
            }
          });
          if (response.ok) {
            const data = await response.json();
            setNotifications(data.data);
          } else {
            console.error('Error al cargar notificaciones:', response.statusText);
          }
        } catch (error) {
          console.error('Error de red al cargar notificaciones:', error);
        }
      }
    };

    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [user, token]); // Dependencia de user y token

  const markAsRead = async (id: number) => { // Tipar 'id' como number
    if (user && token) { // Usar 'token' directamente del contexto
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/notifications/${id}/read`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`, // Usar 'token'
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          setNotifications(prevNotifications =>
            prevNotifications.map(notif =>
              notif.id === id ? { ...notif, leido: true } : notif // Cambiar a true
            )
          );
        } else {
          console.error('Error al marcar como leída:', response.statusText);
        }
      } catch (error) {
        console.error('Error de red al marcar como leída:', error);
      }
    }
  };

  return (
    <>
      <div className="relative">
        <button
          className="relative flex items-center justify-center text-gray-500 transition-colors bg-white border border-gray-200 rounded-full dropdown-toggle hover:text-gray-700 h-11 w-11 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
          onClick={handleClick}
        >
          
          {/* Badge estilo Apple */}
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center text-[11px] font-medium leading-none text-white bg-red-500 rounded-full min-w-[18px] h-[18px] px-1 tracking-tight shadow-sm">
              {unreadNotifications.length}
            </span>
          )}
          {/* Icono de campana más limpio */}
          <svg
            className="fill-current"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C13.1 2 14 2.9 14 4C14 4.1 14 4.2 14 4.3C16.1 5.1 17.5 7 17.5 9.5V15L19 16.5V17.5H5V16.5L6.5 15V9.5C6.5 7 7.9 5.1 10 4.3C10 4.2 10 4.1 10 4C10 2.9 10.9 2 12 2ZM10 20H14C14 21.1 13.1 22 12 22C10.9 22 10 21.1 10 20Z"
              fill="currentColor"
            />
          </svg>
        </button>
        
        {/* Desktop Dropdown */}
        {!isMobile && (
          <Dropdown
            isOpen={isOpen}
            onClose={closeDropdown}
            className="absolute -right-[240px] mt-[17px] flex h-[480px] w-[350px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark sm:w-[361px] lg:right-0"
          >
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                  Notificaciones
                </h5>
              </div>
              <button
                onClick={toggleDropdown}
                className="text-gray-500 transition dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <svg
                  className="fill-current"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            <div className="flex flex-col h-auto overflow-y-auto custom-scrollbar space-y-2">
              {notifications.length > 0 ? (
                (() => {
                  const readNotifications = notifications.filter(n => n.leido);
                  
                  return (
                    <>
                      {unreadNotifications.map((notif) => (
                        <NotificationCard
                          key={notif.id}
                          notification={notif}
                          onMarkAsRead={markAsRead}
                          onClose={closeDropdown}
                          isMobile={false}
                        />
                      ))}
                      
                      {unreadNotifications.length > 0 && readNotifications.length > 0 && (
                        <div className="flex items-center py-2">
                          <div className="flex-1 border-t border-gray-200 dark:border-gray-600"></div>
                          <span className="px-3 text-xs text-gray-500 dark:text-gray-400">Anteriores</span>
                          <div className="flex-1 border-t border-gray-200 dark:border-gray-600"></div>
                        </div>
                      )}
                      
                      {readNotifications.map((notif) => (
                        <NotificationCard
                          key={notif.id}
                          notification={notif}
                          onMarkAsRead={markAsRead}
                          onClose={closeDropdown}
                          isMobile={false}
                        />
                      ))}
                    </>
                  );
                })()
              ) : (
                <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                  No hay notificaciones nuevas.
                </div>
              )}
            </div>
            <Link
              to="/notifications"
              className="block px-4 py-2 mt-3 text-sm font-medium text-center text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
            >
              Ver todas las notificaciones
            </Link>
          </Dropdown>
        )}
      </div>
      
      {/* Mobile Overlay */}
      {isMobile && (
        <MobileNotificationOverlay
          isOpen={isOpen}
          onClose={closeDropdown}
          notifications={notifications}
          onMarkAsRead={markAsRead}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
        />
      )}
    </>
  );
}
